import { DndContext, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { Button } from '../../../components/ui/Button'
import { TaskCard } from '../shared/TaskCard'
import type { TaskStatusDef, TaskSummary } from '../../../api/types'
import styles from './KanbanBoard.module.css'

interface KanbanBoardProps {
  statuses: TaskStatusDef[]
  tasks: TaskSummary[]
  onOpenTask: (taskId: string) => void
  onMoveTask: (taskId: string, statusId: string) => void
  onCreateInStatus: (statusId: string) => void
  canCreate: boolean
}

function DraggableCard({ task, onOpenTask }: { task: TaskSummary; onOpenTask: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id })
  return (
    <div ref={setNodeRef}>
      <TaskCard task={task} onClick={() => onOpenTask(task.id)} dragHandleProps={{ ...attributes, ...listeners }} isDragging={isDragging} />
    </div>
  )
}

function Column({
  status,
  tasks,
  onOpenTask,
  onCreateInStatus,
  canCreate,
}: {
  status: TaskStatusDef
  tasks: TaskSummary[]
  onOpenTask: (id: string) => void
  onCreateInStatus: (statusId: string) => void
  canCreate: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id })

  return (
    <div ref={setNodeRef} className={[styles.column, isOver ? styles.columnOver : ''].join(' ')}>
      <div className={styles.columnHeader}>
        <span className={styles.columnDot} style={{ background: status.color ?? 'var(--text-muted)' }} />
        {status.name}
        <span className={styles.columnCount}>{tasks.length}</span>
      </div>
      <div className={styles.columnBody}>
        {tasks.map((task) => (
          <DraggableCard key={task.id} task={task} onOpenTask={onOpenTask} />
        ))}
      </div>
      {canCreate ? (
        <Button variant="ghost" size="sm" className={styles.addBtn} onClick={() => onCreateInStatus(status.id)}>
          + Aufgabe
        </Button>
      ) : null}
    </div>
  )
}

export function KanbanBoard({ statuses, tasks, onOpenTask, onMoveTask, onCreateInStatus, canCreate }: KanbanBoardProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const task = tasks.find((t) => t.id === active.id)
    if (task && task.statusId !== over.id) {
      onMoveTask(String(active.id), String(over.id))
    }
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className={styles.board}>
        {statuses.map((status) => (
          <Column
            key={status.id}
            status={status}
            tasks={tasks.filter((t) => t.statusId === status.id)}
            onOpenTask={onOpenTask}
            onCreateInStatus={onCreateInStatus}
            canCreate={canCreate}
          />
        ))}
      </div>
    </DndContext>
  )
}
